import { type FC } from 'react';

import { Stack, type StackProps } from '../structural/Stack';

import * as types from './types';

export interface EmptyMessageDistributedStackProps extends Omit<
  StackProps,
  'className' | 'direction' | 'gap' | 'position'
> {
  /**
   * Defines the flex gap between the content elements of the {@link EmptyMessage}, which includes
   * the central content (usually an image), the text (the title and description), and the
   * action.
   *
   * @see {@link types.EmptyMessageGap}
   */
  readonly contentGap?: types.EmptyMessageGap;
  /**
   * @default "standard"
   */
  readonly density?: types.EmptyMessageDensity;
}

/**
 * A vertically oriented {@link Stack} that distributes its child elements based on the density,
 * {@link types.EmptyMessageDensity}, and the {@link types.EmptyMessageGap} to control spacing
 * between its child elements.
 *
 * The component exposes a 'className' prop that allows consumers to apply and adjust the gap
 * that is applied to all child elements in the {@link EmptyMessage} in cases where it is not
 * possible to do so with props.
 */
export const EmptyMessageDistributedStack: FC<EmptyMessageDistributedStackProps> = ({
  children,
  contentGap,
  density = types.DefaultEmptyMessageDensity,
  ...props
}) => (
  <Stack
    {...props}
    className={types.EmptyMessageDistributedContentClassName}
    gap={contentGap ?? types.EmptyMessageDensityProperties[density].contentGap}
    position='relative'
  >
    {children}
  </Stack>
);
